import {
  FileArrowDownIcon,
  FileArrowUpIcon,
  InfoIcon,
  PlusCircleIcon,
} from '@phosphor-icons/react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { FileMimeTypes, PaginatedTable } from '@/modules/common';
import LoadingContainer from '@/modules/common/components/loading-container';
import {
  InventoryView,
  inventoryViewOptions,
} from '@/modules/events/constants';
import { FiltersChangeType } from '@/modules/events/hooks';
import {
  EventProductResponseItem,
  ProductSet,
  Size,
} from '@/modules/events/types';

import {
  AddEditProductSetDialog,
  CreateStationDialog,
  InventoryActions,
  InventoryDetailsDialog,
  InventoryTableFilters,
} from './components';
import { useInventoryTab } from './hooks/use-inventory-tab';
import { useProductSets } from './hooks/use-product-sets';

export type SizeRow = Size & {
  parent: EventProductResponseItem;
};

export function InventoryTab() {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isCreateStationDialogOpen, setIsCreateStationDialogOpen] =
    useState(false);
  const [selectedRow, setSelectedRow] = useState<SizeRow | null>(null);
  const [activeView, setActiveView] = useState<InventoryView>(
    InventoryView.Products
  );
  const [isProductSetDialogOpen, setIsProductSetDialogOpen] = useState(false);
  const [selectedProductSet, setSelectedProductSet] =
    useState<ProductSet | null>(null);

  const {
    eventId,
    products,
    columns,
    subRowsColumns,
    stationsQuery,
    isFetching,
    isUploading,
    selectedStationId,
    isGeneratingReport,
    isExportingStationAudit,
    isExportingProductsAudit,
    restTableProps,
    setSelectedStationId,
    pickFileAndUpload,
    getGenerateReportUrl,
    exportStationAudit,
    exportProductsAudit,
  } = useInventoryTab();

  const isProductSetsView = activeView === InventoryView.ProductSets;

  const {
    columns: productSetColumns,
    productSets,
    isFetching: isFetchingProductSets,
    restTableProps: productSetsTableProps,
  } = useProductSets({
    eventId,
    stationId: selectedStationId,
    enabled: isProductSetsView,
  });

  const pickFile = () => {
    fileInputRef.current?.click();
  };

  const openProductSetDialog = (productSet: ProductSet | null) => {
    setSelectedProductSet(productSet);
    setIsProductSetDialogOpen(true);
  };

  const addProductSetButton = (
    <Button
      disabled={!selectedStationId}
      data-testid="add-product-set-button"
      onClick={() => openProductSetDialog(null)}
      startIcon={<PlusCircleIcon size={16} />}
    >
      Add Product Set
    </Button>
  );

  return (
    <LoadingContainer loading={isUploading || stationsQuery.isFetching}>
      <input
        id="inventory-file"
        type="file"
        ref={fileInputRef}
        className="hidden"
        data-testid="upload-inventory-file-input"
        onChange={pickFileAndUpload}
        accept={FileMimeTypes.Csv}
      />
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <p className="font-semibold text-lg">Stations Table</p>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                data-testid="export-station-audit-button"
                onClick={exportStationAudit}
                startIcon={<FileArrowDownIcon size={16} />}
                isLoading={isExportingStationAudit}
              >
                Export station audit
              </Button>
            </TooltipTrigger>
            <TooltipContent>Station audit for the whole event</TooltipContent>
          </Tooltip>
        </div>
        <div className="flex flex-col gap-4">
          <div className="py-5 flex gap-5 items-center justify-between bg-zinc-900 rounded-lg border border-zinc-800 p-4">
            <div className="flex gap-2 items-center">
              <InfoIcon />
              <div className="text-xs">
                <p className="font-semibold">Manage existing stations</p>
                <p>Use the "Stations" button to view or update them.</p>
              </div>
            </div>

            <Button onClick={() => setIsCreateStationDialogOpen(true)}>
              Stations
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {stationsQuery.data.map((station) => (
              <Button
                key={station.id}
                onClick={() => setSelectedStationId(station.id)}
                variant={
                  selectedStationId === station.id ? 'default' : 'outline'
                }
              >
                {station.name}
              </Button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {inventoryViewOptions.map((view) => (
              <Button
                key={view.value}
                data-testid={`inventory-view-${view.value}-button`}
                onClick={() => setActiveView(view.value)}
                variant={activeView === view.value ? 'default' : 'outline'}
              >
                {view.label}
              </Button>
            ))}
          </div>

          {isProductSetsView ? (
            <PaginatedTable<ProductSet>
              data={productSets}
              columns={productSetColumns}
              emptyTitle="No product sets yet."
              emptyDescription="Create a product set from the products of this station."
              isLoading={isFetchingProductSets}
              tableCellClassName="px-2 py-1"
              tableRowClassName="hover:bg-transparent"
              totalCountLabel="product sets"
              sizeStyleProperty="minWidth"
              onClick={openProductSetDialog}
              disabledColumnsIdsToClick={['actions']}
              getRowId={(row) => row.id}
              emptyButton={addProductSetButton}
              {...productSetsTableProps}
            >
              <div className="flex gap-4 items-center justify-end w-full">
                {addProductSetButton}
              </div>
            </PaginatedTable>
          ) : (
            <PaginatedTable<
              EventProductResponseItem,
              Size & {
                parent: EventProductResponseItem;
              }
            >
              data={products}
              subRowKey="sizes"
              subRowColumns={subRowsColumns}
              columns={columns}
              emptyTitle="Upload inventory from CSV file."
              emptyDescription="The inventory will be uploaded here. Please upload new ones."
              isLoading={isFetching}
              tableCellClassName="px-2 py-1"
              tableRowClassName="hover:bg-transparent"
              onSubRowClick={setSelectedRow}
              totalCountLabel="products"
              disabledColumnsIdsToClick={['actions']}
              disabledSubColumnsIdsToClick={['amount', 'action']}
              getRowId={(row) => row.id}
              emptyButton={
                <Button
                  disabled={isFetching}
                  type="button"
                  onClick={pickFile}
                  data-testid="upload-inventory-button"
                >
                  <FileArrowUpIcon />
                  Upload CSV
                </Button>
              }
              {...restTableProps}
            >
              <div className="flex gap-4 items-center justify-center">
                <InventoryTableFilters
                  filters={restTableProps.filters}
                  onFilters={restTableProps.setFilters as FiltersChangeType}
                />
                <div className="flex gap-2">
                  <Button
                    disabled={isFetching}
                    variant="outline"
                    type="button"
                    onClick={pickFile}
                    data-testid="upload-inventory-button"
                  >
                    <FileArrowUpIcon />
                    Upload CSV
                  </Button>

                  {products.length > 0 && (
                    <InventoryActions
                      eventId={eventId}
                      stationId={selectedStationId}
                      onGenerateReport={getGenerateReportUrl}
                      isGeneratingReport={isGeneratingReport}
                      onExportProductsAudit={exportProductsAudit}
                      isExportingProductsAudit={isExportingProductsAudit}
                      onSuccess={() => restTableProps.refetch()}
                    />
                  )}
                </div>
              </div>
            </PaginatedTable>
          )}
        </div>

        <CreateStationDialog
          isOpen={isCreateStationDialogOpen}
          eventId={eventId}
          stations={stationsQuery.data || []}
          onOpenChange={setIsCreateStationDialogOpen}
        />

        {selectedRow && (
          <InventoryDetailsDialog
            isOpen={!!selectedRow}
            onOpenChange={() => setSelectedRow(null)}
            selectedInventory={selectedRow}
            eventId={eventId}
            stationId={selectedStationId}
            onSuccess={() => restTableProps.refetch()}
          />
        )}

        {isProductSetDialogOpen && (
          <AddEditProductSetDialog
            key={selectedProductSet?.id ?? 'new'}
            isOpen={isProductSetDialogOpen}
            eventId={eventId}
            stationId={selectedStationId}
            productSet={selectedProductSet}
            onOpenChange={(open) => {
              setIsProductSetDialogOpen(open);
              if (!open) {
                setSelectedProductSet(null);
              }
            }}
            onSuccess={() => productSetsTableProps.refetch()}
          />
        )}
      </div>
    </LoadingContainer>
  );
}
